import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';

// PACKAGES
import { useLocation, useNavigate } from 'react-router-dom';
import { Accordion, Dropdown, Modal, Spinner } from 'react-bootstrap';

// COMPONENTS
import TitleSection from '../../components/TitleSection/TitleSection';

// UTILS
import { convertToKebabCase, guid } from '../../libs/utils';
import CroppedImage from '../../components/_Helpers/CroppedImage';
import SvgMask from '../../components/_Helpers/SvgMask';
import DelayedFadeIn from '../../components/_Helpers/DelayedFadeIn';
import { AnalyticsPushBooksAssignedToStudents } from '../../components/_Helpers/AnalyticsEvents';

// TYPES
import apibridge from '../../apibridge';
import { SortOptionsType } from '../../components/TitleSection/SortDropdown';
import { BookListSeries, StudentAdminBookListStage } from '../../api/models';
import { UserType } from '../../store/slice/User';
import { useAppSelector } from '../../store/slice';

const StudentAssignBooks: FC = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const user: UserType = useAppSelector((state) => state.user);

	const { state } = location;
	const { selectedStudents, classId, studentNames } = state as {
		selectedStudents?: string[];
		classId?: string;
		studentNames?: string[];
	};

	const trimmedStudentNames = (studentNames ?? []).map((n) => n.trim()).filter(Boolean);
	const pageTitle = trimmedStudentNames.length === 1 ? trimmedStudentNames[0] : 'Assign books';

	const [dropdownStages, setDropdownStages] = useState<SortOptionsType[]>([]);
	const [stageIds, setStageIds] = useState<string[]>([]);
	const [seriesIds, setSeriesIds] = useState<string[]>([]);
	const [bookIds, setBookIds] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState('');
	const [selectedBookIds, setSelectedBookIds] = useState<string[]>([]);

	const [activeKeys, setActiveKeys] = useState<string[]>([]);
	const [initialBookIds, setInitialBookIds] = useState<string[]>([]);
	const [initialStageIds, setInitialStageIds] = useState<string[]>([]);
	const [initialSeriesIds, setInitialSeriesIds] = useState<string[]>([]);
	const [bookStages, setBookStages] = useState<StudentAdminBookListStage[]>([]);
	const [initialSelectedBookIds, setInitialSelectedBookIds] = useState<string[]>([]); // ALL initially selected books
	const [showSummaryModal, setShowSummaryModal] = useState(false);

	const navigate = useNavigate();

	const getBookStages = async () => {
		setLoading(true);

		const response = await apibridge.postStudentAdminBookList({ studentProfileIds: selectedStudents || [] });
		if (response && response.data && !response.data.isError && response.data.result?.stages) {
			let tempStages = [];

			for (const stage of response.data.result.stages) {
				if (!stage.id || !stage.name) continue;

				tempStages.push({
					value: stage.id,
					label: stage.name
				});
			}

			setBookStages(response.data.result.stages || []);
			setDropdownStages(tempStages);

			const assignedBookIds: string[] = [];
			const assignedStageIds: string[] = [];
			const assignedSeriesIds: string[] = [];
			const individualBookIds: string[] = [];

			for (const stage of response.data.result.stages) {
				if (stage.assigned && stage.id) {
					assignedStageIds.push(stage.id);
				}

				for (const series of stage.serieses || []) {
					if (series.assigned && series.id && !stage.assigned) {
						assignedSeriesIds.push(series.id);
					}

					for (const book of series.books || []) {
						if (book.assigned && book.id) {
							assignedBookIds.push(book.id);

							if (!stage.assigned && !series.assigned) {
								individualBookIds.push(book.id);
							}
						}
					}
				}
			}

			setSelectedBookIds(assignedBookIds);
			setStageIds(assignedStageIds);
			setSeriesIds(assignedSeriesIds);
			setBookIds(individualBookIds);

			// Save initial state for comparison later when Post
			setInitialStageIds(assignedStageIds);
			setInitialSeriesIds(assignedSeriesIds);
			setInitialBookIds(individualBookIds);
			setInitialSelectedBookIds(assignedBookIds); // Track ALL initially selected books

			// Set initial active accordion keys based on which stages have selected books
			const initialActiveKeys: string[] = [];
			response.data.result.stages.forEach((stage, index) => {
				const hasSelectedBook = stage.serieses?.some((series) =>
					series.books?.some((book) => assignedBookIds.includes(book.id || ''))
				);

				if (hasSelectedBook) {
					initialActiveKeys.push(stage.id + '-' + index);
				}
			});
			setActiveKeys(initialActiveKeys);
		}

		setLoading(false);
	};

	useEffect(() => {
		getBookStages();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Get all book IDs from a stage
	const getBooksFromStage = (stage: StudentAdminBookListStage): string[] => {
		return stage.serieses?.flatMap((series) => series.books?.map((book) => book.id || '').filter(Boolean) || []) || [];
	};

	// Helper: Get all book IDs from a series
	const getBooksFromSeries = (series: BookListSeries): string[] => {
		return series.books?.map((book) => book.id || '').filter(Boolean) || [];
	};

	// Helper: Check if all books in an array are selected
	const areAllBooksSelected = (bookIds: string[]): boolean => {
		return bookIds.length > 0 && bookIds.every((id) => selectedBookIds.includes(id));
	};

	/**
	 * Handler for clicking on a stage in the dropdown menu
	 *
	 * @param stage The stage that was clicked
	 */
	const handleClickStage = (stage: StudentAdminBookListStage) => {
		const stageId = stage.id || '';
		const seriesFromStage: string[] = stage?.serieses?.map((series) => series.id || '').filter(Boolean) || [];
		const booksFromStageSeries = getBooksFromStage(stage);
		const allBooksSelected = areAllBooksSelected(booksFromStageSeries);

		// If all books from the stage are selected (either from stageIds or all individually/via series selected), unselect them
		if (allBooksSelected) {
			setStageIds(stageIds.filter((id) => id !== stageId));
			// Remove any series from this stage
			setSeriesIds(seriesIds.filter((id) => !seriesFromStage.includes(id)));
			// Remove individual book selections from this stage
			setBookIds(bookIds.filter((id) => !booksFromStageSeries.includes(id)));
			// Remove from selectedBookIds
			setSelectedBookIds(selectedBookIds.filter((id) => !booksFromStageSeries.includes(id)));
		} else {
			// Otherwise, add the stage ID to the selected stage IDs
			setStageIds([...stageIds, stageId]);
			// Clear any series selections from this stage
			setSeriesIds(seriesIds.filter((id) => !seriesFromStage.includes(id)));
			// Clear any individual book selections from this stage
			setBookIds(bookIds.filter((id) => !booksFromStageSeries.includes(id)));
			// Add all books from the stage to selectedBookIds
			setSelectedBookIds((prev) => [...prev, ...booksFromStageSeries.filter((id) => !prev.includes(id))]);
		}
	};

	/**
	 * Handler for clicking on a series in the dropdown menu
	 *
	 * @param series The series that was clicked
	 * @param stageId The ID of the stage that the series belongs to
	 */
	const handleClickSeries = (series: BookListSeries, stageId: string) => {
		const booksFromSeries = getBooksFromSeries(series);

		// If the stage ID is in the selected stage IDs, we need to convert to individual series selection
		if (stageIds.includes(stageId)) {
			setStageIds(stageIds.filter((id) => id !== stageId));

			// Add all series from this stage EXCEPT the clicked one to seriesIds
			const stage = bookStages.find((s) => s.id === stageId);
			const otherSeriesFromStage = stage?.serieses?.filter((s) => s.id !== series.id) || [];
			const otherSeriesIds = otherSeriesFromStage.map((s) => s.id || '');
			setSeriesIds([...seriesIds, ...otherSeriesIds]);

			// Remove books from the clicked series from selectedBookIds, keep all others
			setSelectedBookIds(selectedBookIds.filter((id) => !booksFromSeries.includes(id)));
			return;
		}

		const allBooksSelected = areAllBooksSelected(booksFromSeries);

		// If the series books are all selected (either from seriesIds or all individually selected), unselect them
		if (allBooksSelected) {
			setSeriesIds(seriesIds.filter((id) => id !== series.id));
			// Remove individual book selections from this series
			setBookIds(bookIds.filter((id) => !booksFromSeries.includes(id)));
			// Remove from selectedBookIds
			setSelectedBookIds(selectedBookIds.filter((id) => !booksFromSeries.includes(id)));
		} else {
			// Otherwise, add the series ID to the selected series IDs
			setSeriesIds([...seriesIds, series.id || '']);
			// Clear any individual book selections from this series
			setBookIds(bookIds.filter((id) => !booksFromSeries.includes(id)));
			// Add all books from the series to selectedBookIds
			setSelectedBookIds((prev) => [...prev, ...booksFromSeries.filter((id) => !prev.includes(id))]);
		}
	};

	/**
	 * Handler for clicking on a book in the list of books
	 *
	 * @param bookId The ID of the book that was clicked
	 * @param seriesId The ID of the series that the book belongs to
	 * @param stageId The ID of the stage that the series belongs to
	 */
	const handleClickBook = (bookId: string, seriesId: string, stageId: string) => {
		// Get the books from the series and stage
		const series = bookStages.find((s) => s.id === stageId)?.serieses?.find((s) => s.id === seriesId);
		const booksFromSeries = series ? getBooksFromSeries(series) : [];

		// If the series ID is in the selected series IDs, we need to convert to individual book selection
		if (seriesIds.includes(seriesId)) {
			setSeriesIds(seriesIds.filter((id) => id !== seriesId));

			const otherBooksFromSeries = booksFromSeries.filter((id) => id !== bookId);
			setBookIds([...bookIds, ...otherBooksFromSeries]);

			setSelectedBookIds(selectedBookIds.filter((id) => id !== bookId));
			return;
		}

		// If the stage ID is in the selected stage IDs, we need to convert to individual series/book selection
		if (stageIds.includes(stageId)) {
			setStageIds(stageIds.filter((id) => id !== stageId));

			// Get the stage
			const stage = bookStages.find((s) => s.id === stageId);

			// Add all series from this stage EXCEPT the one containing the clicked book
			const otherSeriesFromStage = stage?.serieses?.filter((s) => s.id !== seriesId) || [];
			const otherSeriesIds = otherSeriesFromStage.map((s) => s.id || '');
			setSeriesIds([...seriesIds, ...otherSeriesIds]);

			// Add all books from the current series EXCEPT the clicked one to bookIds
			const otherBooksFromSeries = booksFromSeries.filter((id) => id !== bookId);
			setBookIds([...bookIds, ...otherBooksFromSeries]);

			// Remove the clicked book from selectedBookIds, keep all others from the stage
			setSelectedBookIds(selectedBookIds.filter((id) => id !== bookId));
			return;
		}

		// Normal book toggle (no parent stage or series selected)
		if (bookIds.includes(bookId)) {
			// Uncheck the book
			setBookIds(bookIds.filter((id) => id !== bookId));
			setSelectedBookIds(selectedBookIds.filter((id) => id !== bookId));
		} else {
			// Check the book
			setBookIds([...bookIds, bookId]);
			setSelectedBookIds([...selectedBookIds, bookId]);
		}
	};

	const goToStage = (stageId: string) => {
		const element = document.getElementById(`stage-${stageId}`);

		if (element) {
			setTimeout(() => {
				element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 0);
		}
	};

	/**
	 * Calculate session changes: newly selected and unassigned books
	 */
	const getSessionChanges = () => {
		const newlySelected = selectedBookIds.filter((id) => !initialSelectedBookIds.includes(id));
		const unassigned = initialSelectedBookIds.filter((id) => !selectedBookIds.includes(id));
		return { newlySelected, unassigned };
	};

	/**
	 * Revert to initial state (used for error dismissal)
	 */
	const revertToInitialState = () => {
		setSelectedBookIds([...initialSelectedBookIds]);
		setStageIds([...initialStageIds]);
		setSeriesIds([...initialSeriesIds]);
		setBookIds([...initialBookIds]);
		setSearchText('');
	};

	const isSeriesFullySelected = (series: BookListSeries): boolean => {
		const booksFromSeries = getBooksFromSeries(series);
		return areAllBooksSelected(booksFromSeries);
	};

	const isStageFullySelected = (stage: StudentAdminBookListStage): boolean => {
		const booksFromStage = getBooksFromStage(stage);
		return areAllBooksSelected(booksFromStage);
	};

	const handleAssignBooks = async () => {
		const currentStageIds: string[] = [];
		const currentSeriesIds: string[] = [];
		const currentBookIds: string[] = [];

		for (const stage of bookStages) {
			if (stage.id && isStageFullySelected(stage)) {
				currentStageIds.push(stage.id);
			} else {
				for (const series of stage.serieses || []) {
					if (series.id && isSeriesFullySelected(series)) {
						currentSeriesIds.push(series.id);
					} else {
						for (const book of series.books || []) {
							if (book.id && selectedBookIds.includes(book.id)) {
								currentBookIds.push(book.id);
							}
						}
					}
				}
			}
		}

		const assignStageIds = currentStageIds.filter((id) => !initialStageIds.includes(id));
		const assignSeriesIds = currentSeriesIds.filter((id) => !initialSeriesIds.includes(id));
		const assignBookIds = currentBookIds.filter((id) => !initialBookIds.includes(id));

		// Calculate unassignBookIds: books that were initially selected but are no longer selected
		// This catches books that were part of an assigned stage/series but are now unchecked
		const unassignBookIds = initialSelectedBookIds.filter((id) => !selectedBookIds.includes(id));

		const response = await apibridge.postStudentAdminBookManage({
			studentProfileIds: selectedStudents,
			assignStageIds,
			assignSeriesIds,
			assignBookIds,
			unassignStageIds: [],
			unassignSeriesIds: [],
			unassignBookIds
		});

		if (response && response.data) {
			if (!response.data.isError) {
				// Get all selected books
				const selectedBooks = bookStages
					.flatMap((stage) => stage.serieses || [])
					.flatMap((series) => series.books || [])
					.filter((book) => selectedBookIds.includes(book.id || ''));

				// Push analytics for each book and student combination
				AnalyticsPushBooksAssignedToStudents({
					user,
					bookData: {
						books: selectedBooks.map((book) => ({
							book_name: book.name || '',
							book_id: book.id || ''
						}))
					},
					studentData: selectedStudents?.map((studentId) => ({ studentId })) || []
				});

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Book assignment',
						description: 'Books successfully assigned.'
					})
				);

				// Push to student list
				navigate(`/student-management/class/${classId}`);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assign book error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	// Calculate session changes for display
	const { newlySelected, unassigned } = getSessionChanges();
	const hasChanges = newlySelected.length > 0 || unassigned.length > 0;

	return (
		<div className={`class-assign-books-page ${showSummaryModal ? 'summary-modal-open' : ''}`}>
			<TitleSection
				backToText="Back to student list"
				backToUrl={`/student-management/class/${classId}`}
				title={pageTitle}
				description="Use the checkboxes to select a Stage or expand the Stage and select individual books to assign."
			>
				<div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
					<Dropdown className="sort-dropdown">
						<Dropdown.Toggle variant="dropdown">Go to Stage</Dropdown.Toggle>
						<Dropdown.Menu>
							{dropdownStages.map((item, index) => (
								<Dropdown.Item key={index} onClick={() => goToStage(item.label)} as="button">
									Stage {item.label}
								</Dropdown.Item>
							))}
						</Dropdown.Menu>
					</Dropdown>

					<div className="d-flex align-items-center gap-3">
						{hasChanges && (
							<div className={`selected-items-wrapper ${showSummaryModal ? 'selected-items-wrapper-active' : ''}`}>
								<div className="d-flex align-items-center row-gap-2 column-gap-4">
									<div className="d-flex gap-3 align-items-center flex-grow-1 h-24">
										{unassigned.length > 0 && (
											<div className="d-flex align-items-center gap-3">
												<SvgMask path="/svg/caution-solid.svg" width={20} height={20} />

												<p className="body-normal mb-0">
													<strong>{unassigned.length} removed</strong>
												</p>
											</div>
										)}

										{unassigned.length > 0 && newlySelected.length > 0 && <div className="line"></div>}

										{newlySelected.length > 0 && (
											<p className="body-normal mb-0">
												<strong>{newlySelected.length} selected</strong>
											</p>
										)}
									</div>

									<button
										type="button"
										onClick={() => setShowSummaryModal(true)}
										className="btn btn-link align-self-center"
									>
										Save
									</button>
								</div>

								<button onClick={revertToInitialState} className="btn btn-reset reset-select" title="Revert changes">
									<SvgMask path="/svg/cross-thin.svg" width={24} height={24} />
								</button>
							</div>
						)}
						<div className="search-input-wrapper">
							<input
								type="text"
								className="form-control search-input"
								placeholder="Filter by book title"
								onChange={(e) => {
									const target = e.target as HTMLInputElement;
									setSearchText(target.value);
								}}
							/>
						</div>
					</div>
				</div>
			</TitleSection>
			<section className="container">
				{loading ? (
					<div className="d-flex align-items-center justify-content-center">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : (
					<Accordion
						className="book-stage-accordion gap-3-5 d-flex flex-column"
						activeKey={activeKeys}
						onSelect={(eventKeys) => setActiveKeys(eventKeys as string[])}
						alwaysOpen
					>
						{bookStages.map((stage, index) => {
							const stageName = convertToKebabCase(stage.name || '');

							return (
								<Accordion.Item
									key={stage.id}
									id={`stage-${stage.name}`}
									eventKey={stage.id + '-' + index}
									className={`stage-${stage.name}`}
								>
									<Accordion.Header>
										<div
											onClick={(e) => {
												e.stopPropagation();
												handleClickStage(stage);
											}}
											className={`stage-label btn-reset d-flex align-items-center gap-2-5 stage-${stage.name}`}
										>
											<input
												type="checkbox"
												name={`stage-${stage.name}`}
												id={`stage-${stageName}`}
												checked={stageIds.includes(stage.id || '') || isStageFullySelected(stage)}
												onChange={() => handleClickStage(stage)}
											/>
											<label htmlFor={`stage-${stageName}`} className="h4 m-0">
												Stage {stage.name}
											</label>
										</div>
										{stage.attributes && (
											<div className="d-flex attributes">
												{stage.attributes.map((attribute, index) => {
													return (
														<div key={index} className="d-flex attribute-item">
															<p className="m-0 text-primary fw-medium">{attribute.name}</p>
															{attribute.children?.map((child, index) => {
																return (
																	<div key={index} className="attribute-btn text-primary">
																		{child}
																	</div>
																);
															})}
														</div>
													);
												})}
											</div>
										)}
									</Accordion.Header>
									<Accordion.Body className="py-3">
										{stage.serieses?.map((series) => {
											const seriesName = convertToKebabCase(series.name || '');
											const books = series?.books?.filter((book) =>
												book?.name?.toLowerCase().includes(searchText.toLowerCase() || '')
											);
											return (
												<div
													className="d-flex flex-column justify-content-center align-items-center py-2 gap-3-5"
													key={series.id}
													data-has-books={!!books?.length || false}
												>
													<div className="d-flex align-items-center gap-3-5">
														<input
															type="checkbox"
															name={series.name}
															id={`series-${seriesName}`}
															onChange={() => handleClickSeries(series, stage.id || '')}
															checked={
																seriesIds.includes(series.id || '') ||
																stageIds.includes(stage.id || '') ||
																isSeriesFullySelected(series)
															}
														/>
														<label htmlFor={`series-${seriesName}`} className="h3 m-0">
															<strong>{series.name}</strong>
														</label>
													</div>
													<div className="d-flex flex-wrap justify-content-center gap-4">
														{series.books
															?.filter((book) => book.name?.toLowerCase().includes(searchText.toLowerCase() || ''))
															.map((book) => {
																const bookName = convertToKebabCase(book.name || '');
																return (
																	<div className="d-flex flex-column align-items-center gap-2" key={book.id}>
																		<div className="position-relative">
																			<label htmlFor={`book-${bookName}`}>
																				<CroppedImage
																					src={book.coverUrl || 'https://via.placeholder.com/150'}
																					width={177}
																					height={219}
																					alt=""
																					className="rounded-3"
																				/>
																			</label>

																			<div className="checkbox-wrapper">
																				<input
																					className="book-checkbox"
																					type="checkbox"
																					name={book.name}
																					id={`book-${bookName}`}
																					checked={
																						bookIds.includes(book.id || '') ||
																						seriesIds.includes(series.id || '') ||
																						stageIds.includes(stage.id || '')
																					}
																					onChange={() =>
																						handleClickBook(book.id || '', series.id || '', stage.id || '')
																					}
																				/>
																			</div>
																		</div>
																		<label htmlFor={`book-${bookName}`} className="m-0">
																			<strong>{book.name}</strong>
																		</label>
																	</div>
																);
															})}
													</div>
												</div>
											);
										})}
									</Accordion.Body>
								</Accordion.Item>
							);
						})}
					</Accordion>
				)}
			</section>

			{/* Assign Books Summary modal */}
			<Modal show={showSummaryModal} onHide={() => setShowSummaryModal(false)} centered>
				<Modal.Body>
					<h2 className="m-0 text-shades-800">
						<strong>Summary</strong>
					</h2>

					<div className="d-flex flex-column gap-3">
						{newlySelected.length > 0 && (
							<div className="d-flex align-items-center gap-2">
								<img src="/svg/book-add-blue.svg" width={24} height={24} alt="" />

								<p className="fw-bold body-normal m-0">
									Assign {newlySelected.length} new book{newlySelected.length === 1 ? '' : 's'}
								</p>
							</div>
						)}

						{unassigned.length > 0 && (
							<div className="d-flex align-items-start justify-content-start gap-2">
								<img src="/svg/book-remove-red.svg" width={24} height={24} alt="" />

								<div className="d-flex flex-column align-items-start justify-content-start">
									<p className="fw-bold body-normal text-start mb-1">
										Remove {unassigned.length} previously assigned book{unassigned.length === 1 ? '' : 's'}
									</p>

									<p className="body-small text-start m-0">
										Removing books from the student’s eLibrary will reset their progress.
									</p>
								</div>
							</div>
						)}
					</div>

					<div className="button-group d-flex gap-2">
						<button type="button" onClick={() => setShowSummaryModal(false)} className="btn btn-white btn-lg w-50">
							Cancel
						</button>

						<button
							type="button"
							onClick={async () => {
								setShowSummaryModal(false);
								await handleAssignBooks();
							}}
							className="btn btn-lg w-50"
						>
							Save and Continue
						</button>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default StudentAssignBooks;
